import { ClientInput } from "../../shared/interfaces";
import { MIDDLE_MILE_AIR, SPD, LTL } from "../../shared/strings/constants";
import { getTrackingDetails, getTrackingDetailsForSupplyChain } from "../apiLinks";
import { strToBool } from "../strToBool";

/**
 * Builds the request body for the GetTrackingResponsesForSupplyChain API.
 * Supports two lookup modes:
 *   1. By trackingRequestId (the RTTV internal UUID) — used when no identifierType is specified
 *   2. By indexableAttributes (key + value) — used when identifierType and identifierValue are provided
 *
 * Also supports grantless mode (isGrantless flag) and optional tenantId.
 */
const buildSupplyChainRequestBody = (clientInput: ClientInput) => {
    const { trackingId, tenantId, identifierType, identifierValue, isGrantless } = clientInput;

    const body: Record<string, unknown> = {};

    // If identifierType and identifierValue are provided, use indexableAttributes lookup
    if (identifierType && identifierValue) {
        body.IndexableAttributes = [{ Key: identifierType, Value: identifierValue }];
    } else {
        body.TrackingId = trackingId;
    }

    // TenantId is optional for grantless mode
    if (tenantId) {
        body.TenantId = tenantId;
    }

    // Pass isGrantless flag if enabled
    if (strToBool(isGrantless)) {
        body.IsGrantless = true;
    }

    return body;
};

/**
 * Makes an API request to fetch shipment details based on the logistics mode.
 * For MIDDLE_MILE_AIR mode, uses the supply chain tracking API.
 * For other modes, uses the default tracking API.
 *
 * @param clientInput - Client configuration including tracking details and mode
 * @param authToken - Authorization token for API requests
 * @param apiKey - API key for request authentication
 * @returns Promise resolving to the API response
 */
export const getShipmentDetailsRequest = async (clientInput: ClientInput, authToken: string, apiKey: string) => {
    const { region, trackingId, carrierId, tenantId, logisticsMode } = clientInput;

    if([MIDDLE_MILE_AIR, SPD, LTL].includes(logisticsMode)) {
        const getTrackingDetailsForSupplyChainResponse = await fetch(getTrackingDetailsForSupplyChain(region), {
        method: "POST",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            "Content-Type": "application/json",
            "Authorization": authToken,
            "x-api-key": apiKey
        },
        redirect: "follow",
        body: JSON.stringify(buildSupplyChainRequestBody(clientInput))
      }).then(response => {
        if(response.status === 500) {
          throw new Error("Internal Server Error");
        } else if(response.status === 401) {
          return { errorType: "Token Expired or Unauthorized" };
        } else if (response.status === 403) {
          return { errorType: "Invalid X-API_Token" };
        } else if (response.status === 400) {
          return { errorType: "Invalid inputs" };
        }
        return response.json();
      });
      return getTrackingDetailsForSupplyChainResponse;
    } else {
        const getShipmentDetailsResponse = await fetch(getTrackingDetails(region), {
        method: "POST",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            "Content-Type": "application/json",
            "Authorization": authToken,
            "x-api-key": apiKey
        },
        redirect: "follow",
        body: JSON.stringify({
            "TrackingId": trackingId,
            "CarrierId": carrierId,
            "TenantId": tenantId
        })
      }).then(response => {
        if(response.status === 500) {
          throw new Error("Internal Server Error");
        } else if(response.status === 401) {
          return { errorType: "Token Expired or Unauthorized" };
        } else if (response.status === 403) {
          return { errorType: "Invalid X-API_Token" };
        }
        return response.json();
      });
      return getShipmentDetailsResponse;
    }
    
};
